import './Landing.css'
import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { writeText as clipboardWriteText } from '@molecule/app-clipboard'
import { getClient } from '@molecule/app-http'
import { useTheme } from '@molecule/app-react'

import MoleculeHero, { type MoleculeActivity } from '../components/MoleculeHero.js'
import { MoleculeSpinner } from '../components/MoleculeSpinner.js'
import { SiteFooter } from '../components/SiteFooter.js'
import { usePublishFixedTopOffset } from '../hooks/useFixedTopOffset.js'
import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { prefetchOnIntent, PROMPT_OPENS_IDE, scheduleIdlePrefetch } from '../lib/prefetch.js'
import { promptFromLocation } from '../lib/prompt-link.js'
import { beginPromptMorph, settleViewportForMorph } from '../lib/promptMorph.js'

/**
 * The "everything a real app needs" list: label, icon, the registry category
 * it opens on /packages (null when none), and one line for the hover tip.
 */
const CAPABILITIES: Array<{ label: string; icon: string; category: string | null; info: string }> =
  [
    {
      label: 'Auth & OAuth',
      icon: 'shieldLock',
      category: 'auth',
      info: 'Sessions, passwords and OAuth sign-in behind one interface.',
    },
    {
      label: 'Two-factor auth',
      icon: 'key',
      category: 'two-factor',
      info: 'TOTP and recovery codes, wired into the same sign-in flow.',
    },
    {
      label: 'Payments',
      icon: 'creditCard',
      category: 'payments',
      info: 'Checkout, subscriptions, invoices and webhooks; the payment provider is a bond.',
    },
    {
      label: 'Databases',
      icon: 'database',
      category: 'database',
      info: 'A typed data store with migrations; the database engine is a bond.',
    },
    {
      label: 'Caching',
      icon: 'zap',
      category: 'cache',
      info: 'Caching through one API; the store behind it is a bond.',
    },
    {
      label: 'AI & agents',
      icon: 'sparkleFill',
      category: 'ai',
      info: 'One AI interface with tools, streaming and agents; the model provider is a bond.',
    },
    {
      label: 'Email',
      icon: 'mail',
      category: 'emails',
      info: 'Transactional email and templates; the delivery provider is a bond.',
    },
    {
      label: 'SMS',
      icon: 'comment',
      category: 'sms',
      info: 'Texts and verification codes; the SMS provider is a bond.',
    },
    {
      label: 'Search',
      icon: 'search',
      category: 'search',
      info: 'Full-text and semantic search; the engine is a bond.',
    },
    {
      label: 'Realtime',
      icon: 'broadcast',
      category: 'realtime',
      info: 'Live updates over WebSockets or server-sent events.',
    },
    {
      label: 'Background jobs',
      icon: 'sync',
      category: 'queue',
      info: 'Queues and workers with retries; BullMQ and others.',
    },
    {
      label: 'Scheduling',
      icon: 'clock',
      category: 'cron',
      info: 'Cron and scheduled tasks through one interface.',
    },
    {
      label: 'Uploads',
      icon: 'upload',
      category: 'file-upload',
      info: 'File uploads to S3-compatible storage or disk, with image handling.',
    },
    {
      label: 'Analytics',
      icon: 'graph',
      category: 'analytics',
      info: 'One tracking interface on both ends; the provider is a bond.',
    },
    {
      label: 'Logging & monitoring',
      icon: 'pulse',
      category: 'monitoring',
      info: 'Structured logs, error tracking, health checks and uptime probes.',
    },
    {
      label: 'Push notifications',
      icon: 'bell',
      category: 'push-notifications',
      info: 'Web and mobile push through one bond.',
    },
    {
      label: 'Localization (i18n)',
      icon: 'globe',
      category: 'i18n',
      info: 'Every UI string through t(); locale packages ship dozens of languages.',
    },
    {
      label: 'Accessibility (a11y)',
      icon: 'accessibilityInset',
      category: 'ui',
      info: 'Semantic components, focus handling and contrast built into the UI kit.',
    },
    {
      label: 'Testing',
      icon: 'beaker',
      category: 'testing',
      info: 'Unit, integration and Playwright end-to-end tests, scaffolded with the app.',
    },
    {
      label: 'CI/CD & deploys',
      icon: 'workflow',
      category: 'ci',
      info: 'CI workflows for the major git hosts, and one-click deploys from molecule.dev.',
    },
  ]

/** Inline octicon set used across the landing sections. */
const ICONS: Record<string, string> = {
  comment: `<path d="M1.5 4.25c0-.966.784-1.75 1.75-1.75h17.5c.966 0 1.75.784 1.75 1.75v12.5a1.75 1.75 0 0 1-1.75 1.75h-9.69l-3.573 3.573A1.458 1.458 0 0 1 5 21.043V18.5H3.25a1.75 1.75 0 0 1-1.75-1.75ZM3.25 4a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h2.5a.75.75 0 0 1 .75.75v3.19l3.72-3.72a.749.749 0 0 1 .53-.22h10a.25.25 0 0 0 .25-.25V4.25a.25.25 0 0 0-.25-.25Z"/>`,
  shieldLock: `<path d="M11.46 1.137a1.748 1.748 0 0 1 1.08 0l8.25 2.675A1.75 1.75 0 0 1 22 5.476V10.5c0 6.19-3.77 10.705-9.401 12.83a1.704 1.704 0 0 1-1.198 0C5.771 21.204 2 16.69 2 10.5V5.476c0-.76.49-1.43 1.21-1.664Zm.617 1.426a.253.253 0 0 0-.154 0L3.673 5.24a.25.25 0 0 0-.173.237V10.5c0 5.461 3.28 9.483 8.43 11.426a.199.199 0 0 0 .14 0c5.15-1.943 8.43-5.965 8.43-11.426V5.476a.25.25 0 0 0-.173-.237ZM13 12.232V15a1 1 0 0 1-2 0v-2.768a2 2 0 1 1 2 0Z"/>`,
  creditCard: `<path d="M15.25 14a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5h-3.5Z"/><path d="M1.75 3h20.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 22.25 21H1.75A1.75 1.75 0 0 1 0 19.25V4.75C0 3.784.784 3 1.75 3Zm-.25 7v9.25c0 .138.112.25.25.25h20.5a.25.25 0 0 0 .25-.25V10Zm0-5.25V8.5h21V4.75a.25.25 0 0 0-.25-.25H1.75a.25.25 0 0 0-.25.25Z"/>`,
  database: `<path d="M12 1.25c2.487 0 4.773.402 6.466 1.079.844.337 1.577.758 2.112 1.264.536.507.922 1.151.922 1.907v12.987l-.026.013h.026c0 .756-.386 1.4-.922 1.907-.535.506-1.268.927-2.112 1.264-1.693.677-3.979 1.079-6.466 1.079s-4.774-.402-6.466-1.079c-.844-.337-1.577-.758-2.112-1.264C2.886 19.9 2.5 19.256 2.5 18.5h.026l-.026-.013V5.5c0-.756.386-1.4.922-1.907.535-.506 1.268-.927 2.112-1.264C7.226 1.652 9.513 1.25 12 1.25ZM4 14.371v4.116l-.013.013H4c0 .211.103.487.453.817.351.332.898.666 1.638.962 1.475.589 3.564.971 5.909.971 2.345 0 4.434-.381 5.909-.971.739-.296 1.288-.63 1.638-.962.349-.33.453-.607.453-.817h.013L20 18.487v-4.116a7.85 7.85 0 0 1-1.534.8c-1.693.677-3.979 1.079-6.466 1.079s-4.774-.402-6.466-1.079a7.843 7.843 0 0 1-1.534-.8ZM20 12V7.871a7.85 7.85 0 0 1-1.534.8C16.773 9.348 14.487 9.75 12 9.75s-4.774-.402-6.466-1.079A7.85 7.85 0 0 1 4 7.871V12c0 .21.104.487.453.817.35.332.899.666 1.638.961 1.475.59 3.564.972 5.909.972 2.345 0 4.434-.382 5.909-.972.74-.295 1.287-.629 1.638-.96.35-.33.453-.607.453-.818ZM4 5.5c0 .211.103.487.453.817.351.332.898.666 1.638.962 1.475.589 3.564.971 5.909.971 2.345 0 4.434-.381 5.909-.971.739-.296 1.288-.63 1.638-.962.349-.33.453-.607.453-.817 0-.211-.103-.487-.453-.817-.351-.332-.898-.666-1.638-.962-1.475-.589-3.564-.971-5.909-.971-2.345 0-4.434.381-5.909.971-.739.296-1.288.63-1.638.962C4.104 5.013 4 5.29 4 5.5Z"/>`,
  sparkleFill: `<path d="M11.296 1.924c.24-.656 1.168-.656 1.408 0l.717 1.958a11.25 11.25 0 0 0 6.697 6.697l1.958.717c.657.24.657 1.168 0 1.408l-1.958.717a11.25 11.25 0 0 0-6.697 6.697l-.717 1.958c-.24.657-1.168.657-1.408 0l-.717-1.958a11.25 11.25 0 0 0-6.697-6.697l-1.958-.717c-.656-.24-.656-1.168 0-1.408l1.958-.717a11.25 11.25 0 0 0 6.697-6.697l.717-1.958Z"/>`,
  mail: `<path d="M1.75 3h20.5c.966 0 1.75.784 1.75 1.75v14a1.75 1.75 0 0 1-1.75 1.75H1.75A1.75 1.75 0 0 1 0 18.75v-14C0 3.784.784 3 1.75 3ZM1.5 7.412V18.75c0 .138.112.25.25.25h20.5a.25.25 0 0 0 .25-.25V7.412l-9.52 6.433c-.592.4-1.368.4-1.96 0Zm0-2.662v.852l10.36 7a.25.25 0 0 0 .28 0l10.36-7V4.75a.25.25 0 0 0-.25-.25H1.75a.25.25 0 0 0-.25.25Z"/>`,
  search: `<path d="M10.25 2a8.25 8.25 0 0 1 6.34 13.53l5.69 5.69a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215l-5.69-5.69A8.25 8.25 0 1 1 10.25 2ZM3.5 10.25a6.75 6.75 0 1 0 13.5 0 6.75 6.75 0 0 0-13.5 0Z"/>`,
  broadcast: `<path d="M20.485 2.515a.75.75 0 0 0-1.06 1.06A10.465 10.465 0 0 1 22.5 11c0 2.9-1.174 5.523-3.075 7.424a.75.75 0 0 0 1.06 1.061A11.965 11.965 0 0 0 24 11c0-3.314-1.344-6.315-3.515-8.485Zm-15.91 1.06a.75.75 0 0 0-1.06-1.06A11.965 11.965 0 0 0 0 11c0 3.313 1.344 6.314 3.515 8.485a.75.75 0 0 0 1.06-1.06A10.465 10.465 0 0 1 1.5 11c0-2.9 1.174-5.524 3.075-7.425ZM8.11 7.11a.75.75 0 0 0-1.06-1.06A6.98 6.98 0 0 0 5 11a6.98 6.98 0 0 0 2.05 4.95.75.75 0 0 0 1.06-1.061 5.48 5.48 0 0 1-1.61-3.89 5.48 5.48 0 0 1 1.61-3.888Zm8.84-1.06a.75.75 0 1 0-1.06 1.06A5.48 5.48 0 0 1 17.5 11a5.48 5.48 0 0 1-1.61 3.889.75.75 0 1 0 1.06 1.06A6.98 6.98 0 0 0 19 11a6.98 6.98 0 0 0-2.05-4.949ZM14 11a2 2 0 0 1-1.25 1.855v8.395a.75.75 0 0 1-1.5 0v-8.395A2 2 0 1 1 14 11Z"/>`,
  upload: `<path d="M4 20.25V18a.75.75 0 0 1 1.5 0v2.25c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V18a.75.75 0 0 1 1.5 0v2.25A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25Z"/><path d="M5.22 9.53a.749.749 0 0 1 0-1.06l6.25-6.25a.749.749 0 0 1 1.06 0l6.25 6.25a.749.749 0 1 1-1.06 1.06l-4.97-4.969V16.75a.75.75 0 0 1-1.5 0V4.561L6.28 9.53a.749.749 0 0 1-1.06 0Z"/>`,
  graph: `<path d="M2.5 2.75a.75.75 0 0 0-1.5 0v18.5c0 .414.336.75.75.75H20a.75.75 0 0 0 0-1.5H2.5V2.75Z"/><path d="M22.28 7.78a.75.75 0 0 0-1.06-1.06l-5.72 5.72-3.72-3.72a.75.75 0 0 0-1.06 0l-6 6a.75.75 0 1 0 1.06 1.06l5.47-5.47 3.72 3.72a.75.75 0 0 0 1.06 0l6.25-6.25Z"/>`,
  bell: `<path d="M12 1c3.681 0 7 2.565 7 6v4.539c0 .642.189 1.269.545 1.803l2.2 3.298A1.517 1.517 0 0 1 20.482 19H15.5a3.5 3.5 0 1 1-7 0H3.519a1.518 1.518 0 0 1-1.265-2.359l2.2-3.299A3.25 3.25 0 0 0 5 11.539V7c0-3.435 3.318-6 7-6ZM6.5 7v4.539a4.75 4.75 0 0 1-.797 2.635l-2.2 3.298-.003.01.001.007.004.006.006.004.007.001h16.964l.007-.001.006-.004.004-.006.001-.006a.017.017 0 0 0-.003-.01l-2.199-3.299a4.753 4.753 0 0 1-.798-2.635V7c0-2.364-2.383-4.5-5.5-4.5S6.5 4.636 6.5 7ZM14 19h-4a2 2 0 1 0 4 0Z"/>`,
  globe: `<path d="M12 1c6.075 0 11 4.925 11 11s-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1Zm3.241 10.5v-.001c-.1-2.708-.992-4.904-1.89-6.452a13.919 13.919 0 0 0-1.304-1.88L12 3.11l-.047.059c-.354.425-.828 1.06-1.304 1.88-.898 1.547-1.79 3.743-1.89 6.451Zm-12.728 0h4.745c.1-3.037 1.1-5.49 2.093-7.204.39-.672.78-1.233 1.119-1.673C6.11 3.329 2.746 7 2.513 11.5Zm18.974 0C21.254 7 17.89 3.329 13.53 2.623c.339.44.729 1.001 1.119 1.673.993 1.714 1.993 4.167 2.093 7.204ZM8.787 13c.182 2.478 1.02 4.5 1.862 5.953.382.661.818 1.29 1.304 1.88l.047.057.047-.059c.354-.425.828-1.06 1.304-1.88.842-1.451 1.679-3.471 1.862-5.951Zm-1.504 0H2.552a9.505 9.505 0 0 0 7.918 8.377 15.773 15.773 0 0 1-1.119-1.673C8.413 18.085 7.47 15.807 7.283 13Zm9.434 0c-.186 2.807-1.13 5.085-2.068 6.704-.39.672-.78 1.233-1.118 1.673A9.506 9.506 0 0 0 21.447 13Z"/>`,
  accessibilityInset: `<path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm2.5 6.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM12 9h6.375a.75.75 0 0 1 0 1.5h-4.028l.542 4.42.482 4.5a.75.75 0 1 1-1.492.16l-.41-3.83H10.53l-.41 3.83a.75.75 0 1 1-1.492-.16l.484-4.511.54-4.409H5.625a.75.75 0 0 1 0-1.5H12Z"/>`,
  law: `<path d="M12.75 2.75V4.5h1.975c.351 0 .694.106.984.303l1.697 1.154c.041.028.09.043.14.043h4.102a.75.75 0 0 1 0 1.5H20.07l3.366 7.68a.749.749 0 0 1-.23.896c-.1.074-.203.143-.31.206a6.296 6.296 0 0 1-.79.399 7.349 7.349 0 0 1-2.856.569 7.343 7.343 0 0 1-2.855-.568 6.205 6.205 0 0 1-.79-.4 3.205 3.205 0 0 1-.307-.202l-.005-.004a.749.749 0 0 1-.23-.896l3.368-7.68h-.886c-.351 0-.694-.106-.984-.303l-1.697-1.154a.246.246 0 0 0-.14-.043H12.75v14.5h4.487a.75.75 0 0 1 0 1.5H6.763a.75.75 0 0 1 0-1.5h4.487V6H9.275a.249.249 0 0 0-.14.043L7.439 7.197c-.29.197-.633.303-.984.303h-.886l3.368 7.68a.75.75 0 0 1-.209.878c-.08.065-.16.126-.31.223a6.077 6.077 0 0 1-.792.433 6.924 6.924 0 0 1-2.876.62 6.913 6.913 0 0 1-2.876-.62 6.077 6.077 0 0 1-.792-.433 3.483 3.483 0 0 1-.309-.221.762.762 0 0 1-.21-.88L3.93 7.5H2.353a.75.75 0 0 1 0-1.5h4.102c.05 0 .099-.015.141-.043l1.695-1.154c.29-.198.634-.303.985-.303h1.974V2.75a.75.75 0 0 1 1.5 0ZM2.193 15.198a5.414 5.414 0 0 0 2.557.635 5.414 5.414 0 0 0 2.557-.635L4.75 9.368Zm14.51-.024c.082.04.174.083.275.126.53.223 1.305.45 2.272.45a5.847 5.847 0 0 0 2.547-.576L19.25 9.367Z"/>`,
  meter: `<path d="M12 2.5a9.5 9.5 0 1 0 9.5 9.5c0-1.406-.305-2.74-.852-3.939a.75.75 0 0 1 1.364-.622C22.648 8.829 23 10.374 23 12c0 6.075-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1c1.626 0 3.17.353 4.561.988a.75.75 0 1 1-.622 1.364A9.463 9.463 0 0 0 12 2.5Zm9.03.47a.75.75 0 0 1 0 1.06l-6.445 6.446a3 3 0 1 1-1.06-1.06L19.97 2.97a.75.75 0 0 1 1.06 0ZM12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/>`,
  check: `<path d="M21.03 5.72a.75.75 0 0 1 0 1.06l-11.5 11.5a.747.747 0 0 1-1.072-.012l-5.5-5.75a.75.75 0 1 1 1.084-1.036l4.97 5.195L19.97 5.72a.75.75 0 0 1 1.06 0Z"/>`,
  key: `<path d="M16.75 8.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z"/><path d="M15.75 0a8.25 8.25 0 1 1-2.541 16.101l-1.636 1.636a1.744 1.744 0 0 1-1.237.513H9.25a.25.25 0 0 0-.25.25v1.448a.876.876 0 0 1-.256.619l-.214.213a.75.75 0 0 1-.545.22H5.25a.25.25 0 0 0-.25.25v1A1.75 1.75 0 0 1 3.25 24h-1.5A1.75 1.75 0 0 1 0 22.25v-2.836c0-.464.185-.908.513-1.236l7.386-7.388A8.249 8.249 0 0 1 15.75 0ZM9 8.25a6.733 6.733 0 0 0 .463 2.462.75.75 0 0 1-.168.804l-7.722 7.721a.25.25 0 0 0-.073.177v2.836c0 .138.112.25.25.25h1.5a.25.25 0 0 0 .25-.25v-1c0-.966.784-1.75 1.75-1.75H7.5v-1c0-.966.784-1.75 1.75-1.75h1.086a.25.25 0 0 0 .177-.073l1.971-1.972a.75.75 0 0 1 .804-.168A6.75 6.75 0 1 0 9 8.25Z"/>`,
  zap: `<path d="M15.716 1.329a1.341 1.341 0 0 1 2.109 1.55L15.147 9h4.161c1.623 0 2.372 2.016 1.143 3.075L8.102 22.721a1.148 1.148 0 0 1-1.81-1.317L8.996 15H4.674c-1.619 0-2.37-2.008-1.148-3.07l12.19-10.6Zm.452 1.595L4.51 13.061a.25.25 0 0 0 .164.439h5.45a.749.749 0 0 1 .692 1.041l-2.559 6.066 11.215-9.668a.25.25 0 0 0-.164-.439H14a.75.75 0 0 1-.687-1.05Z"/>`,
  sync: `<path d="M3.38 8A9.502 9.502 0 0 1 12 2.5a9.502 9.502 0 0 1 9.215 7.182.75.75 0 1 0 1.456-.364C21.473 4.539 17.15 1 12 1a10.995 10.995 0 0 0-9.5 5.452V4.75a.75.75 0 0 0-1.5 0V8.5a1 1 0 0 0 1 1h3.75a.75.75 0 0 0 0-1.5H3.38Zm-.595 6.318a.75.75 0 0 0-1.455.364C2.527 19.461 6.85 23 12 23c4.052 0 7.592-2.191 9.5-5.451v1.701a.75.75 0 0 0 1.5 0V15.5a1 1 0 0 0-1-1h-3.75a.75.75 0 0 0 0 1.5h2.37A9.502 9.502 0 0 1 12 21.5c-4.446 0-8.181-3.055-9.215-7.182Z"/>`,
  clock: `<path d="M12.5 7.25a.75.75 0 0 0-1.5 0v5.5c0 .27.144.518.378.651l3.5 2a.75.75 0 0 0 .744-1.302L12.5 12.315V7.25Z"/><path d="M12 1c6.075 0 11 4.925 11 11s-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1ZM2.5 12a9.5 9.5 0 0 0 9.5 9.5 9.5 9.5 0 0 0 9.5-9.5A9.5 9.5 0 0 0 12 2.5 9.5 9.5 0 0 0 2.5 12Z"/>`,
  pulse: `<path d="M9.002 2.5a.75.75 0 0 1 .691.464l6.302 15.305 2.56-6.301a.75.75 0 0 1 .695-.468h4a.75.75 0 0 1 0 1.5h-3.495l-3.06 7.532a.75.75 0 0 1-1.389.004L8.997 5.21l-3.054 7.329A.75.75 0 0 1 5.25 13H.75a.75.75 0 0 1 0-1.5h4l3.558-8.538a.75.75 0 0 1 .694-.462Z"/>`,
  beaker: `<path d="M8 8.807V3.5h-.563a.75.75 0 0 1 0-1.5h9.125a.75.75 0 0 1 0 1.5H16v5.307l5.125 9.301c.964 1.75-.302 3.892-2.299 3.892H5.174c-1.998 0-3.263-2.142-2.3-3.892ZM4.189 18.832a1.123 1.123 0 0 0 .985 1.668h13.652a1.123 1.123 0 0 0 .985-1.668L17.7 15H6.3ZM14.5 3.5h-5V9a.75.75 0 0 1-.093.362L7.127 13.5h9.746l-2.28-4.138A.75.75 0 0 1 14.5 9Z"/>`,
  workflow: `<path d="M1 3a2 2 0 0 1 2-2h6.5a2 2 0 0 1 2 2v6.5a2 2 0 0 1-2 2H7v4.063C7 16.355 7.644 17 8.438 17H12.5v-2.5a2 2 0 0 1 2-2H21a2 2 0 0 1 2 2V21a2 2 0 0 1-2 2h-6.5a2 2 0 0 1-2-2v-2.5H8.437A2.939 2.939 0 0 1 5.5 15.562V11.5H3a2 2 0 0 1-2-2Zm2-.5a.5.5 0 0 0-.5.5v6.5a.5.5 0 0 0 .5.5h6.5a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5ZM14.5 14a.5.5 0 0 0-.5.5V21a.5.5 0 0 0 .5.5H21a.5.5 0 0 0 .5-.5v-6.5a.5.5 0 0 0-.5-.5Z"/>`,
  pullRequest: `<path d="M16 19.25a3.25 3.25 0 1 1 6.5 0 3.25 3.25 0 0 1-6.5 0Zm-14.5 0a3.25 3.25 0 1 1 6.5 0 3.25 3.25 0 0 1-6.5 0Zm0-14.5a3.25 3.25 0 1 1 6.5 0 3.25 3.25 0 0 1-6.5 0ZM4.75 3a1.75 1.75 0 1 0 .001 3.501A1.75 1.75 0 0 0 4.75 3Zm0 14.5a1.75 1.75 0 1 0 .001 3.501A1.75 1.75 0 0 0 4.75 17.5Zm14.5 0a1.75 1.75 0 1 0 .001 3.501 1.75 1.75 0 0 0-.001-3.501Z"/><path d="M13.405 1.72a.75.75 0 0 1 0 1.06L12.185 4h4.065A3.75 3.75 0 0 1 20 7.75v8.75a.75.75 0 0 1-1.5 0V7.75a2.25 2.25 0 0 0-2.25-2.25h-4.064l1.22 1.22a.75.75 0 0 1-1.061 1.06l-2.5-2.5a.75.75 0 0 1 0-1.06l2.5-2.5a.75.75 0 0 1 1.06 0ZM4.75 7.25A.75.75 0 0 1 5.5 8v8A.75.75 0 0 1 4 16V8a.75.75 0 0 1 .75-.75Z"/>`,
}
function Icon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="m-ico"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  )
}

/** The prompt the "How Molecule works" graphic builds; the hero prefills it when linked. */
const DEMO_PROMPT =
  'An AI travel agent that books flights by voice, rebooks when they’re delayed, and splits the bill.'

/** What Synthase picks for that prompt, and what each package gives you. */
const BUILD_PACKAGES: ReadonlyArray<{ name: string; info: string }> = [
  {
    name: 'api-ai',
    info: 'One AI interface with tools and streaming; the model provider is a swappable bond.',
  },
  {
    name: 'api-flights',
    info: 'Flight search, booking and status behind one interface; providers as bonds.',
  },
  {
    name: 'api-ai-speech',
    info: 'Speech to text and text to speech, so the agent can listen and talk back.',
  },
  {
    name: 'api-payments',
    info: 'Checkout, subscriptions and split bills; the payment provider is a bond.',
  },
  { name: 'api-sms', info: 'Texts and rebooking alerts; the SMS provider is a bond.' },
  {
    name: 'app-react',
    info: 'The React binding: hooks for auth, i18n, theme and routing over framework-agnostic cores.',
  },
  {
    name: 'app-analytics',
    info: 'Track once against the interface; the analytics provider is a bond.',
  },
  {
    name: 'app-react-native',
    info: 'The same app on iOS and Android, with native device bonds.',
  },
]
const BUILD_STEPS = [
  'Picked 8 packages from the catalog',
  'Wired them like hand-written code',
  'Type-checked, linted and tested',
  'Live preview ready',
]

/** The database bonds the slot cycles through. */
const DB_BONDS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'postgresql', label: 'PostgreSQL' },
  { id: 'mysql', label: 'MySQL' },
  { id: 'sqlite', label: 'SQLite' },
  { id: 'd1', label: 'Cloudflare D1' },
]
/** Categories that follow the same core + bond pattern; each links to its catalog section. */
const BOND_CATEGORIES = [
  'auth',
  'payments',
  'emails',
  'ai',
  'analytics',
  'realtime',
  'file-upload',
  'search',
  'queue',
  'sms',
  'i18n',
  'logger',
]

/** The app's own loop, clockwise from the top left. */
const LOOP_STEPS = [
  { label: 'people use the app', x: 14.6, y: 14.6 },
  { label: 'analytics · errors · feedback', x: 85.4, y: 14.6 },
  { label: 'the AI reads the signals', x: 85.4, y: 85.4 },
  { label: 'improves · ships · measures', x: 14.6, y: 85.4 },
]
const LOOP_SIGNALS: ReadonlyArray<{ kind: string; text: string; search: string }> = [
  {
    kind: 'analytics',
    text: 'Where your users drop off, and which features they use',
    search: 'analytics',
  },
  {
    kind: 'error tracking',
    text: 'Every error your users hit, with its stack trace',
    search: 'error tracking',
  },
  {
    kind: 'feedback',
    text: 'What your users ask for, in their own words',
    search: 'user feedback',
  },
]

/**
 * A command that copies itself when clicked (or activated with the keyboard),
 * through the bonded clipboard, and says "Copied" for a moment. Plain markup
 * on purpose: the landing bundle carries no ClassMap.
 */
function CopyCommand({ command }: { command: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const copy = (): void => {
    clipboardWriteText(command)
      .then(() => {
        setCopied(true)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 1600)
      })
      .catch((error: unknown) => {
        console.warn('landing: could not copy the command', error)
      })
  }
  return (
    // mol-bespoke-button: a code block that copies itself, not a CTA; its look is the m-cmd block.
    <button
      type="button"
      className={`m-cmd m-cmd--copy${copied ? ' is-copied' : ''}`}
      onClick={copy}
      aria-label={`Copy ${command}`}
      data-mol-id="landing-copy-command"
      data-command={command}
    >
      <code>{command}</code>
      <span className="m-cmd-hint" aria-live="polite">
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  )
}

/** The three ways to start; the same catalog and the same agent behind each. */
const WAYS: ReadonlyArray<{
  label: string
  title: string
  body: string
  command?: string
  /** Where to read about this way in depth (the docs surface for it). */
  docs?: { to: string; label: string }
}> = [
  {
    label: 'molecule.dev',
    title: 'Describe it in the IDE',
    body: 'Synthase builds, tests and deploys it in a workspace you can share.',
  },
  {
    label: 'Command line',
    title: 'Scaffold it with mlcl',
    body: 'The catalog wired into a plain project, in your own editor.',
    command: 'mlcl create my-app',
    docs: { to: '/mlcl#mlcl-command-map', label: 'CLI reference' },
  },
  {
    label: 'Any coding agent',
    title: 'Give your agent the catalog',
    body: 'The MCP server hands any agent the packages, their docs and the scaffolder.',
    command: 'mlcl mcp install',
    docs: { to: '/mlcl#mlcl-agents', label: 'MCP setup' },
  },
]
const ACCENTS = ['var(--acc1)', 'var(--acc2)', 'var(--acc3)', 'var(--acc4)']
const accent = (i: number) => ACCENTS[i % ACCENTS.length]

// Two marquee rows scrolling in opposing directions, each with a distinct
// intent so a visitor sees both things Molecule does:
//   ROW 1 — "add this to my EXISTING app" (integrate/upgrade a running product;
//            our core audience: teams adopting production AI tooling).
//   ROW 2 — "build a BRAND-NEW app" (start from zero across diverse verticals).
// No prompt should appear in both rows.
//
// EVERY prompt must map to something molecule.dev OFFICIALLY supports so a click
// leads to something Synthase can actually build: ROW 1 to a real @molecule/*
// integration (registry.json), ROW 2 to a real flagship template
// (mlcl/templates/apps/). Prompts naming an unsupported integration/template were
// removed or trimmed — see docs/landing-suggestions-removed.md to re-add them
// when support lands. Do NOT add a prompt for an unsupported capability.
const EXAMPLE_ROW_1 = [
  'Add subscription billing to our SaaS',
  'Add Google and GitHub SSO to our existing app',
  'Add an AI assistant to our customer dashboard',
  'Wire Slack alerts into our deployment pipeline',
  'Add semantic search over our support tickets',
  'Add SOC 2 audit logging to our API',
  'Add role-based access control to our admin panel',
  'Add push notifications to our mobile app',
  'Add a public REST API with API keys to our product',
  'Add file uploads with S3 to our product',
  'Add two-factor auth to our login flow',
  'Add webhooks so customers can subscribe to events',
  'Add a customer billing portal',
  'Add real-time presence to our collaboration tool',
  'Add rate limiting to our public endpoints',
]
const EXAMPLE_ROW_2 = [
  'A CRM with social sign-in and billing',
  'An AI support agent over my docs with citations',
  'A telehealth booking app with reminders',
  'A realtime chat app with presence and push',
  'A marketplace with seller payouts and reviews',
  'A project tracker with kanban boards and webhooks',
  'A personal finance dashboard',
  'A booking page for my barbershop',
  'An invoicing tool with PDF export and payments',
  'A habit tracker with streaks and reminders',
  'A job board with applicant tracking',
  'An IoT dashboard for my home sensors',
  'A subscription box service with recurring orders',
  'An e-learning platform with quizzes and certificates',
]

const PromptForm = memo(function PromptForm({
  activity,
  onEarly,
}: {
  activity: React.MutableRefObject<MoleculeActivity>
  onEarly: (prompt: string) => void
}) {
  const [prompt, setPrompt] = useState('')
  const taRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  // Phones have no Shift+Enter: on coarse pointers Enter inserts a newline and
  // the arrow button is the submit affordance. Desktop keeps Enter-to-submit.
  const coarsePointer = useMediaQuery('(pointer: coarse)')
  // Touch marquee pause (no hover on phones): a tap sets this briefly so a
  // moving example chip can be tapped; cleared after a few seconds.
  const [marqueePaused, setMarqueePaused] = useState(false)
  const marqueePauseTimer = useRef<number | undefined>(undefined)
  const pauseMarquee = useCallback(() => {
    setMarqueePaused(true)
    window.clearTimeout(marqueePauseTimer.current)
    marqueePauseTimer.current = window.setTimeout(() => setMarqueePaused(false), 4000)
  }, [])
  useEffect(() => () => window.clearTimeout(marqueePauseTimer.current), [])

  // A shared link can arrive with the prompt in it (see lib/prompt-link.ts):
  // prefill the box and select the text, so one keystroke replaces it and
  // Enter sends it as-is.
  useEffect(() => {
    const text = promptFromLocation(window.location.hash, window.location.search)
    if (!text) return
    setPrompt(text)
    activity.current.length = text.length
    activity.current.lastTypeAt = performance.now()
    // Select once React has written the value; selecting before that collapses.
    selectOnPrefill.current = true
  }, [activity])
  const selectOnPrefill = useRef(false)
  useEffect(() => {
    const ta = taRef.current
    if (!selectOnPrefill.current || !ta || ta.value !== prompt || !prompt) return
    selectOnPrefill.current = false
    ta.focus()
    ta.select()
  }, [prompt])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const text = prompt.trim()
      if (!text) return
      // On phones the soft keyboard may be open at submit: blur + let the
      // viewport settle before measuring, or the fixed overlay lands offset.
      // Desktop runs synchronously (unchanged).
      settleViewportForMorph(taRef.current, () => {
        // Begin the prompt → chat morph from the whole prompt box (textarea +
        // submit button), so the floating textarea starts at the width the user
        // sees — not the narrower textarea-only width.
        const rect = formRef.current?.getBoundingClientRect()
        if (rect) {
          beginPromptMorph(
            { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
            text,
          )
        }
        onEarly(text)
      })
    },
    [prompt, onEarly],
  )

  const pickExample = useCallback(
    (text: string) => {
      setPrompt(text)
      activity.current.length = text.length
      activity.current.lastTypeAt = performance.now()
      const ta = taRef.current
      if (ta) {
        ta.focus()
        const end = text.length
        requestAnimationFrame(() => ta.setSelectionRange(end, end))
      }
    },
    [activity],
  )

  return (
    <div className="hero-prompt">
      <form ref={formRef} onSubmit={handleSubmit} className="landing-prompt-form">
        <textarea
          ref={taRef}
          id="hero-prompt-input"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value)
            activity.current.length = e.target.value.length
            activity.current.lastTypeAt = performance.now()
          }}
          onFocus={() => {
            activity.current.focused = true
            // Focusing the prompt is strong intent: warm the IDE chunk now so
            // it's cached by the time they submit.
            prefetchOnIntent()
          }}
          onBlur={() => {
            activity.current.focused = false
          }}
          placeholder="Describe what to build or integrate..."
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !coarsePointer) {
              e.preventDefault()
              handleSubmit(e)
            }
          }}
        />
        <button
          type="submit"
          disabled={!prompt.trim()}
          aria-label="Start building with this prompt"
        >
          <svg
            viewBox="0 0 16 16"
            height="20"
            width="20"
            fill="currentColor"
            className="octicon octicon-arrow-right"
            aria-hidden="true"
            role="img"
            style={{
              display: 'inline-block',
              userSelect: 'none',
              verticalAlign: 'text-bottom',
              overflow: 'visible',
            }}
          >
            <path
              d="M8.22 2.97a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06l2.97-2.97H3.75a.75.75 0 010-1.5h7.44L8.22 4.03a.75.75 0 010-1.06z"
              fillRule="evenodd"
            ></path>
          </svg>
        </button>
      </form>
      <div
        className={`hero-prompt-marquee${marqueePaused ? ' is-paused' : ''}`}
        aria-label="Example prompts"
        onTouchStart={pauseMarquee}
      >
        <ExampleRow items={EXAMPLE_ROW_1} dir="ltr" onPick={pickExample} />
        <ExampleRow items={EXAMPLE_ROW_2} dir="rtl" onPick={pickExample} />
      </div>
    </div>
  )
})

function ExampleRow({
  items,
  dir,
  onPick,
}: {
  items: string[]
  dir: 'ltr' | 'rtl'
  onPick: (text: string) => void
}) {
  return (
    <div className={`hero-prompt-row hero-prompt-row--${dir}`}>
      <div className="hero-prompt-track">
        {[0, 1].map((copy) => (
          <div className="hero-prompt-group" key={copy} aria-hidden={copy === 1 || undefined}>
            {items.map((ex, i) => (
              <button
                type="button"
                key={i}
                className="hero-prompt-example"
                title={ex}
                tabIndex={copy === 1 ? -1 : 0}
                onClick={() => onPick(ex)}
              >
                {ex}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * How long ANY landing dial may run before the client gives up (ms). The fetch
 * client installs NO default timeout (`timeout` is applied only when the caller
 * passes one — @molecule/app-http's provider), and the landing dials twice: the
 * early-access signup POST and the guest hint's `GET /projects` verify. Left
 * unbounded, a request that never settles (a half-open connection, a stalled
 * proxy) fires neither `.then` nor `.catch` — the POST sat on a disabled
 * "Submitting…" forever (no error, no way to re-submit; the only recovery was
 * closing and reopening the modal), and the guest verify never answered at all:
 * the Dashboard CTA stayed unresolved for the whole visit with its connection
 * held open. The bound turns each hang into the ordinary failed dial its catch
 * already handles — the failed signup ("Something went wrong. Please try
 * again.", button re-enabled) and the unproven hint, which stays exactly as it
 * was, the reading every other failed read on this page keeps. Same budget
 * every analytics dial carries (FleetTab's ANALYTICS_POLL_TIMEOUT_MS); a local
 * constant because the landing entry must not import an analytics chunk for it.
 */
const LANDING_DIAL_TIMEOUT_MS = 30_000

function EarlyAccessModal({
  open,
  prompt,
  onClose,
}: {
  open: boolean
  prompt: string
  onClose: () => void
}) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [error, setError] = useState('')
  // The reset below is keyed to the modal OPENING alone. `onClose` arrives as
  // a fresh closure on every Landing render (it is an inline arrow in the
  // caller's JSX), so with it in the deps this effect re-ran — and reset the
  // form to idle — on every parent re-render while the modal was up: a POST
  // still in flight was handed back a re-enabled "Notify me" button (a second
  // click double-submits), and a "done" screen could flip back to the form.
  // The listener reads the latest callback through a ref instead.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // One generation per submitted form. A submission can be superseded while
  // its POST is still in flight — the reader closes the modal (Escape) and
  // reopens it, which resets the form for a NEW session. The old dial's
  // answer settling after that wrote the NEW form's state: "You're on the
  // list." over a form whose submission was never sent (or still in flight),
  // and a failure of the request the reader is looking at could arrive after
  // the stale success and never be shown. A superseded attempt writes
  // nothing; only the attempt the reader is looking at settles its own form.
  const attemptRef = useRef(0)

  useEffect(() => {
    if (!open) return
    attemptRef.current += 1
    setStatus('idle')
    setError('')
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      setError('Please enter a valid email address.')
      setStatus('error')
      return
    }
    setStatus('loading')
    setError('')
    const attempt = ++attemptRef.current
    try {
      // The dial is bounded (LANDING_DIAL_TIMEOUT_MS above): an unbounded one
      // stranded the form on a disabled "Submitting…" for as long as the
      // request hung, with the catch — the only path that re-enables the
      // button — never reached.
      await getClient().post(
        '/early-access',
        { email: value, prompt },
        {
          timeout: LANDING_DIAL_TIMEOUT_MS,
        },
      )
      if (attemptRef.current !== attempt) return
      setStatus('done')
    } catch (error) {
      console.error('early-access signup failed', error)
      if (attemptRef.current !== attempt) return
      setError('Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  return (
    <div className="early-modal-overlay" onClick={onClose}>
      <div
        className="early-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="early-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        {status === 'done' ? (
          <>
            <h3 className="early-modal-title">You&rsquo;re on the list.</h3>
            <p className="early-modal-body">
              Thanks! We&rsquo;ll email you the moment Molecule.dev is live.
            </p>
            <button type="button" className="m-btn m-btn--primary" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <h3 className="early-modal-title">It looks like you&rsquo;re a bit early!</h3>
            <p className="early-modal-body">
              Molecule.dev goes live by the end of June 2026. Enter your email and we&rsquo;ll let
              you know as soon as we&rsquo;ve launched.
            </p>
            <form className="early-modal-form" onSubmit={submit}>
              <input
                type="email"
                className="early-modal-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
              <button
                type="submit"
                className="m-btn m-btn--primary"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Submitting…' : 'Notify me'}
              </button>
            </form>
            {status === 'error' && <p className="early-modal-error">{error}</p>}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The landing's frame: the `<main>` the page paints into, plus the ONE hover
 * tooltip every `data-info` element on the page opens.
 *
 * The tooltip's state lives HERE, not in {@link Landing}, and the page arrives
 * as `children`. A tip appearing, moving between the ~35 `data-info` elements
 * (the capability list, the package chips, the bond slot) or hiding therefore
 * changes THIS component's state alone: the children element is the very
 * object the last `Landing` render produced, so React does not reconcile that
 * subtree again. Held in `Landing`, every hovered item re-rendered the whole
 * page — the app's largest route — to move one 300px box.
 *
 * A `data-info` element shows its text just under itself. The tip is FIXED to
 * the viewport, so it never takes space or moves what follows; it hides on
 * scroll. It stays inside the element carrying the `landing` class
 * (`.landing .m-tip` styles it) — which is why the frame owns that element.
 */
function LandingFrame({
  theme,
  exiting,
  children,
}: {
  /** The theme class the landing paints with. */
  theme: string
  /** True while the prompt morph fades the page out before it navigates. */
  exiting: boolean
  /** The landing's own content. */
  children: ReactNode
}) {
  // One tooltip for the whole page: anything with data-info (a package chip, a
  // capability, a bond) shows it just under itself.
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null)
  const tipItem = useRef<HTMLElement | null>(null)
  const tipPointer = useRef<{ x: number; y: number } | null>(null)
  const placeTip = useCallback((target: HTMLElement | null) => {
    const item = target?.closest<HTMLElement>('[data-info]') ?? null
    if (item === tipItem.current) return
    tipItem.current = item
    if (!item) {
      setTip(null)
      return
    }
    const r = item.getBoundingClientRect()
    const w = Math.min(300, window.innerWidth - 16)
    const x = Math.min(Math.max(r.left, 8), window.innerWidth - w - 8)
    setTip({ text: item.dataset.info ?? '', x, y: r.bottom + 8 })
  }, [])
  const onTipMove = useCallback(
    (e: React.MouseEvent) => {
      tipPointer.current = { x: e.clientX, y: e.clientY }
      placeTip(e.target as HTMLElement)
    },
    [placeTip],
  )
  // A chip can change under a still pointer (the bond slot cycles four), and
  // no mouse event fires for that: re-read what is under the pointer while a
  // tip shows.
  useEffect(() => {
    if (!tip) return
    const id = window.setInterval(() => {
      const p = tipPointer.current
      if (!p) return
      placeTip(document.elementFromPoint(p.x, p.y) as HTMLElement | null)
    }, 250)
    return () => window.clearInterval(id)
  }, [tip, placeTip])
  const onTipFocus = useCallback(
    (e: React.FocusEvent) => placeTip(e.target as HTMLElement),
    [placeTip],
  )
  const hideTip = useCallback(() => {
    tipPointer.current = null
    placeTip(null)
  }, [placeTip])
  useEffect(() => {
    window.addEventListener('scroll', hideTip, { passive: true })
    return () => window.removeEventListener('scroll', hideTip)
  }, [hideTip])

  return (
    <main
      onMouseMove={onTipMove}
      onMouseLeave={hideTip}
      onFocus={onTipFocus}
      onBlur={hideTip}
      className={`landing ${theme}`}
      style={{ opacity: exiting ? 0 : 1, transition: 'opacity 240ms ease' }}
    >
      {children}
      {tip ? (
        <div
          className="m-tip"
          role="tooltip"
          style={{ left: tip.x, top: tip.y }}
          aria-hidden="true"
        >
          {tip.text}
        </div>
      ) : null}
    </main>
  )
}

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false)
  // Mobile: the fixed HealthBanner anchors below this fixed header instead of
  // covering the brand/menu (see useFixedTopOffset).
  const landingHeaderRef = useRef<HTMLElement | null>(null)
  usePublishFixedTopOffset(landingHeaderRef, useIsMobile())
  const { mode: theme, toggleTheme } = useTheme()
  const moleculeActivity = useRef<MoleculeActivity>({ lastTypeAt: 0, length: 0, focused: false })
  const [earlyOpen, setEarlyOpen] = useState(false)
  const [pendingPrompt, setPendingPrompt] = useState('')
  const [exiting, setExiting] = useState(false)
  const navigate = useNavigate()
  // LIVE (2026-08-08): submitting a prompt opens the IDE for everyone
  // (PROMPT_OPENS_IDE). The early-access email branch below is the pre-launch
  // fallback, kept only for the revert path (see PROMPT_OPENS_IDE).
  //
  // The morph's 240 ms fade timer is OWNED: held in a ref, cleared before it
  // re-arms (a double submit armed two navigations) and cleared when the
  // landing unmounts. Left unowned, a navigation that beat the fade — browser
  // Back, a header link inside the 240 ms window — was overridden anyway: the
  // orphaned timer fired after the unmount and yanked the route to
  // /workspace/new over wherever the user had just gone. The happy path never
  // clears it: the landing unmounts as the RESULT of the navigate, so the
  // timer has already fired by then.
  const morphTimer = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      window.clearTimeout(morphTimer.current)
    },
    [],
  )
  const openEarly = useCallback(
    (p: string) => {
      if (PROMPT_OPENS_IDE) {
        // Fade the landing out so the cut into the workspace prompt morph is smooth.
        setExiting(true)
        window.clearTimeout(morphTimer.current)
        morphTimer.current = window.setTimeout(
          () => navigate('/workspace/new', { state: { initialPrompt: p } }),
          240,
        )
        return
      }
      setPendingPrompt(p)
      setEarlyOpen(true)
    },
    [navigate],
  )
  const handleGetStarted = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setMenuOpen(false)
    const ta = document.getElementById('hero-prompt-input') as HTMLTextAreaElement | null
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (ta) ta.focus({ preventScroll: true })
  }, [])
  // Returning users get a Dashboard button instead of Get Started: logged in
  // (the JS-readable `mol_auth=1` presence hint the auth client gates on —
  // server-managed, so trusted instantly with no auth-init flicker) or a guest
  // whose session actually still owns projects. The guest cookie alone is NOT
  // trusted: `mol_guest_uid` outlives the projects behind it (guest cleanup,
  // deletions, dev DB resets), and a stale one showed Dashboard to visitors
  // with nothing there — so the guest path VERIFIES against GET /projects
  // before showing, and clears the stale cookie only when the answer is a
  // CONFIRMED-EMPTY list (a 401), never when the body could not be read.
  const isLoggedIn =
    typeof document !== 'undefined' && /(?:^|;\s*)mol_auth=1(?:;|$)/.test(document.cookie)
  // ONE fact, read ONE way: the signed-in hint is read LIVE, here and on every
  // render — the nav's icon column and its `with-login` class beside the button
  // both read it fresh. Held in state (as the button's mount-time copy was) it
  // went stale the moment the hint moved WITHOUT a remount — a sign-in in
  // another tab, or the auth client clearing a stale hint during its
  // cookie-restore probe (config.ts `shouldRestoreUser` rejects an anonymous
  // row and clears it) — and the header contradicted itself: no "Log in" icon
  // (live) beside a menu still offering "Get Started" (stale), or a Dashboard
  // CTA under a "Log in" icon after a sign-out. Only the VERIFIED guest outcome
  // is state: it is FETCHED, not read off a cookie.
  const [guestHasProjects, setGuestHasProjects] = useState(false)
  const hasDashboard = isLoggedIn || guestHasProjects
  useEffect(() => {
    // The verified guest outcome is a fact about a SESSION, and it may not
    // outlive the session it was verified against — the two arms below are the
    // cases where there is no guest session left to be true of, and the state
    // is dropped with them. Left standing, it was the second of the two
    // contradictions this button's own note names: the claim flow that adopts
    // a guest's projects on sign-in CLEARS `mol_guest_uid`
    // (bonds/auth-default.ts), so a visitor who signed in (the verify's result
    // kept while `mol_auth=1`) and then signed out again — in ANOTHER tab, so
    // nothing remounts — had the icon column reading "Log in" live beside a
    // menu and a pinned shortcut still offering Dashboard.
    if (isLoggedIn) {
      setGuestHasProjects(false)
      return undefined
    }
    if (typeof document === 'undefined' || !/(?:^|;\s*)mol_guest_uid=/.test(document.cookie)) {
      setGuestHasProjects(false)
      return undefined
    }
    let cancelled = false
    void (async () => {
      try {
        // The verify dial is bounded (LANDING_DIAL_TIMEOUT_MS above) — the same
        // budget the signup POST carries: a request that never settles fired
        // neither `.then` nor `.catch`, so the hint stayed unresolved for the
        // whole visit (a guest who owns projects read "Get Started" all visit)
        // with its connection held open. A timed-out read rejects with no
        // status, so the catch's 401 arm does not run and the hint — and the
        // cookie — stay exactly as they were, the failed-read contract below.
        const res = await getClient().get<unknown[]>('/projects', {
          timeout: LANDING_DIAL_TIMEOUT_MS,
        })
        // An UNUSABLE 200 body is a FAILED read, not an empty list — the rule
        // every other reader in the app keeps (FleetTab's isJsonObjectBody /
        // isUsableReport: "an unusable 200 body is a failed poll"). The http
        // client's non-JSON fallback hands back a captive portal's 200 HTML (a
        // hotel/airport wifi login page), a gateway's error document, or a
        // moved shape as a string or an object, and the `Array.isArray(x) ? x :
        // []` this used to read coerced that into "this guest owns nothing" —
        // then DELETED `mol_guest_uid`, the pointer the post-auth claim uses to
        // transfer the guest's projects and the only handle on the guest
        // session. An unreadable read therefore orphaned projects that exist,
        // on a link a public visitor can follow with no account. Only a real
        // ARRAY is a list; anything else leaves the hint exactly as it was.
        if (!Array.isArray(res.data)) return
        if (!cancelled && res.data.length > 0) {
          setGuestHasProjects(true)
          return
        }
        // Confirmed empty (or the guest user is gone): the hint is stale —
        // drop it so it stops implying a dashboard exists. Both halves are the
        // same fact: the POINTER (`mol_guest_uid`, which the claim flow uses)
        // and the verified CTA state, which was true of a session that owns
        // projects and is not true of one that owns none.
        if (!cancelled) {
          document.cookie = 'mol_guest_uid=; Max-Age=0; path=/'
          setGuestHasProjects(false)
        }
      } catch (error) {
        // A 401 is also a confirmed-stale hint (the guest user/session behind
        // the cookie is gone) — clear it, and the CTA state with it (an earlier
        // verify against the session that just 401'd proves nothing now).
        // Anything else (network failure) just leaves the button as it was.
        const status = (error as { response?: { status?: number } })?.response?.status
        if (!cancelled && status === 401) {
          document.cookie = 'mol_guest_uid=; Max-Age=0; path=/'
          setGuestHasProjects(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isLoggedIn])
  const goDashboard = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setMenuOpen(false)
      navigate('/dashboard')
    },
    [navigate],
  )
  const goLogin = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setMenuOpen(false)
      navigate('/login')
    },
    [navigate],
  )
  // Warm the IDE route chunk during idle time after the landing has loaded, so
  // submitting a prompt opens the workspace with no extra download. Runs only
  // when the prompt actually opens the IDE (see PROMPT_OPENS_IDE).
  useEffect(() => scheduleIdlePrefetch(), [])
  // Cards fade in as they scroll into view (once), staggered by their --i.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.landing [data-reveal]'))
    if (els.length === 0) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in')
            io.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.15 },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
  // The page's one hover tooltip lives in {@link LandingFrame}, NOT here: its
  // state changes on every `data-info` element the pointer crosses (~35 of
  // them — the capability list, the package chips, the bond slot), and held
  // here each one re-rendered this whole page (the landing is the app's
  // largest route) for a 300px box. The frame owns the state and takes the
  // page as `children`, whose element identity is stable while this component
  // does not re-render — so a tooltip move leaves the body untouched.

  return (
    <LandingFrame theme={theme} exiting={exiting}>
      <header ref={landingHeaderRef} className="sc-gKsewC fulSCR sc-idOhPF jKBjyX" role="banner">
        <div>
          <div
            id="menu"
            className={`sc-bkzZxe goNxpA hero-scroll-in${menuOpen ? ' open' : ''}${isLoggedIn ? '' : ' with-login'}`}
          >
            <span
              className="sc-iBPRYJ llhqcR"
              id="menu-open-icon"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg
                viewBox="0 0 16 16"
                height="30"
                width="30"
                fill="currentColor"
                className="octicon octicon-three-bars"
                aria-hidden="true"
                role="img"
                style={{
                  display: 'inline-block',
                  userSelect: 'none',
                  verticalAlign: 'text-bottom',
                  overflow: 'visible',
                }}
              >
                <path
                  d="M1 2.75A.75.75 0 011.75 2h12.5a.75.75 0 110 1.5H1.75A.75.75 0 011 2.75zm0 5A.75.75 0 011.75 7h12.5a.75.75 0 110 1.5H1.75A.75.75 0 011 7.75zM1.75 12a.75.75 0 100 1.5h12.5a.75.75 0 100-1.5H1.75z"
                  fillRule="evenodd"
                ></path>
              </svg>
            </span>
            <span
              className="sc-iBPRYJ llhqcR"
              id="menu-close-icon"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg
                viewBox="0 0 24 24"
                height="30"
                width="30"
                fill="currentColor"
                className="octicon octicon-x"
                aria-hidden="true"
                role="img"
                style={{
                  display: 'inline-block',
                  userSelect: 'none',
                  verticalAlign: 'text-bottom',
                  overflow: 'visible',
                }}
              >
                <path
                  d="M5.72 5.72a.75.75 0 011.06 0L12 10.94l5.22-5.22a.75.75 0 111.06 1.06L13.06 12l5.22 5.22a.75.75 0 11-1.06 1.06L12 13.06l-5.22 5.22a.75.75 0 01-1.06-1.06L10.94 12 5.72 6.78a.75.75 0 010-1.06z"
                  fillRule="evenodd"
                ></path>
              </svg>
            </span>
            <button id="theme-toggler" onClick={() => toggleTheme()}>
              <span id="moon-icon">
                <svg
                  viewBox="0 0 24 24"
                  height="30"
                  width="30"
                  fill="currentColor"
                  className="octicon octicon-moon"
                  aria-hidden="false"
                  role="img"
                  style={{
                    display: 'inline-block',
                    userSelect: 'none',
                    verticalAlign: 'text-bottom',
                    overflow: 'visible',
                  }}
                  aria-label="Theme: Dark"
                >
                  <path
                    d="M16.5 6c0 5.799-4.701 10.5-10.5 10.5-.426 0-.847-.026-1.26-.075A8.5 8.5 0 1016.425 4.74c.05.413.075.833.075 1.259zm-1.732-2.04A9.08 9.08 0 0114.999 6a9 9 0 01-11.04 8.768l-.004-.002a9.367 9.367 0 01-.78-.218c-.393-.13-.8.21-.67.602a9.938 9.938 0 00.329.855l.004.01A10.002 10.002 0 0012 22a10.002 10.002 0 004.015-19.16l-.01-.005a9.745 9.745 0 00-.855-.328c-.392-.13-.732.276-.602.67a8.934 8.934 0 01.218.779l.002.005z"
                    fillRule="evenodd"
                  ></path>
                </svg>
              </span>
              <span id="sun-icon">
                <svg
                  viewBox="0 0 24 24"
                  height="30"
                  width="30"
                  fill="currentColor"
                  className="octicon octicon-sun"
                  aria-hidden="false"
                  role="img"
                  style={{
                    display: 'inline-block',
                    userSelect: 'none',
                    verticalAlign: 'text-bottom',
                    overflow: 'visible',
                  }}
                  aria-label="Theme: Light"
                >
                  <path
                    d="M12 17.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11zm0 1.5a7 7 0 100-14 7 7 0 000 14zm12-7a.75.75 0 01-.75.75h-2.5a.75.75 0 010-1.5h2.5A.75.75 0 0124 12zM4 12a.75.75 0 01-.75.75H.75a.75.75 0 010-1.5h2.5A.75.75 0 014 12zm16.485-8.485a.75.75 0 010 1.06l-1.768 1.768a.75.75 0 01-1.06-1.06l1.767-1.768a.75.75 0 011.061 0zM6.343 17.657a.75.75 0 010 1.06l-1.768 1.768a.75.75 0 11-1.06-1.06l1.767-1.768a.75.75 0 011.061 0zM12 0a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0V.75A.75.75 0 0112 0zm0 20a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5A.75.75 0 0112 20zM3.515 3.515a.75.75 0 011.06 0l1.768 1.768a.75.75 0 11-1.06 1.06L3.515 4.575a.75.75 0 010-1.06zm14.142 14.142a.75.75 0 011.06 0l1.768 1.768a.75.75 0 01-1.06 1.06l-1.768-1.767a.75.75 0 010-1.061z"
                    fillRule="evenodd"
                  ></path>
                </svg>
              </span>
            </button>
            {/* Log in — a user icon right of the theme toggle (fades in with
                the header like the other icons). Hidden once logged in. */}
            {!isLoggedIn && (
              <button
                id="login-user-icon"
                onClick={goLogin}
                aria-label="Log in"
                title="Log in"
                data-mol-id="landing-login"
              >
                <svg
                  viewBox="0 0 24 24"
                  height="30"
                  width="30"
                  fill="currentColor"
                  className="octicon octicon-person"
                  aria-hidden="true"
                  role="img"
                  style={{
                    display: 'inline-block',
                    userSelect: 'none',
                    verticalAlign: 'text-bottom',
                    overflow: 'visible',
                  }}
                >
                  <path
                    d="M12 2.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM8 8a4 4 0 118 0 4 4 0 01-8 0zm-3.25 13c0-2.62 2.13-4.75 4.75-4.75h5c2.62 0 4.75 2.13 4.75 4.75v.25a.75.75 0 001.5 0V21c0-3.45-2.8-6.25-6.25-6.25h-5A6.25 6.25 0 003.25 21v.25a.75.75 0 001.5 0V21z"
                    fillRule="evenodd"
                  ></path>
                </svg>
              </button>
            )}
            <ul className="sc-fodVxV erWPyZ">
              <li>
                <a href="/#scale">
                  <span>Scale</span>
                </a>
              </li>
              <li>
                <a href="/#composable">
                  <span>Composable</span>
                </a>
              </li>
              <li>
                <a href="/#capabilities">
                  <span>Capabilities</span>
                </a>
              </li>
              <li>
                <a href="/#faq">
                  <span>FAQ</span>
                </a>
              </li>
              <li>
                {hasDashboard ? (
                  <a href="/dashboard" className="assemble-link" onClick={goDashboard}>
                    <button className="sc-bdfBwQ bzqOOY" type="button">
                      <span>Dashboard</span>
                    </button>
                  </a>
                ) : (
                  <a href="/#top" className="assemble-link" onClick={handleGetStarted}>
                    <button className="sc-bdfBwQ bzqOOY" type="button">
                      <span>Get Started</span>
                    </button>
                  </a>
                )}
              </li>
            </ul>
          </div>
        </div>
        {/* Always-visible Dashboard shortcut for returning users ONLY (logged
            in / guest with a project): pinned where the menu's Dashboard
            button lands, it crossfades OUT over the same scroll range the menu
            fades IN — so a Dashboard button is visible at every scroll
            position without having to scroll to reveal the menu. */}
        {hasDashboard && (
          <a
            href="/dashboard"
            className="header-dashboard-cta"
            onClick={goDashboard}
            data-mol-id="landing-dashboard"
          >
            <button className="sc-bdfBwQ bzqOOY" type="button" tabIndex={-1}>
              <span>Dashboard</span>
            </button>
          </a>
        )}
      </header>
      <div id="top"></div>
      <div className="hero-brand-sticky">
        <div>
          <a href="/#top" className="hero-brand-bg">
            <span className="animate-gradient">Molecule</span>
            <span>.dev</span>
          </a>
        </div>
      </div>
      <section className="sc-dQppl cQZIoF" id="sticky-top">
        <div>
          <div className="molecule-hero-layer hero-scroll-out">
            <MoleculeHero activity={moleculeActivity} theme={theme} />
          </div>
          <section className="sc-dQppl cQZIoF" id="tagline">
            <div>
              <h1 className="hero-tagline hero-scroll-out" id="hero-tagline">
                <span className="hero-brand-spacer" aria-hidden="true">
                  Molecule.dev
                </span>{' '}
                gives AI over a thousand packages it understands.
              </h1>
              <p className="hero-tagline-points hero-scroll-out">
                <span className="point-opensource">Open source.</span>{' '}
                <span className="point-production">Real architecture.</span>{' '}
                <span className="point-ship">Ship in minutes.</span>{' '}
                <span className="point-scale">Built to scale.</span>
              </p>
              <div>
                <PromptForm activity={moleculeActivity} onEarly={openEarly} />
              </div>
            </div>
          </section>
          {/*
            Devices/screenshots showcase. Kept in source for future reference,
            but intentionally NOT rendered. (It was previously display:none, which
            still downloaded all assets on every landing load; then false&&, which
            esbuild dead-code-eliminated but triggered no-constant-binary-expression.)
            To restore, move this back into live JSX and swap display:none for the
            real condition.

            <section className="sc-dQppl cQZIoF" id="devices" style={{ display: 'none' }}>
            <div>
              <picture id="laptop-outline">
                <img alt="Macbook Air" src="/images/devices/macbook-air.webp" />
              </picture>
              <picture id="phone-outline">
                <div id="phone-background"></div>
                <img alt="iPhone" src="/images/devices/iphone-outline.webp" />
              </picture>
              <figure>
                <picture>
                  <img alt="iPhone-lockscreen" src="/images/devices/iphone-lockscreen.webp" />
                </picture>
                <picture>
                  <img
                    alt="User account menu"
                    src="/images/screenshots/dark-user-account-menu.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="Password changes and two-factor authentication setup"
                    src="/images/screenshots/dark-user-auth-flow.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="In-app purchases with renewal and expiration logic for subscriptions"
                    src="/images/screenshots/dark-user-billing-subscription-plans.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="Push notifications with painless setup"
                    src="/images/screenshots/dark-user-push-notifications.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="Device manager with authentication token revocation and push notification status"
                    src="/images/screenshots/dark-user-device-manager.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="Current device storage, network information, and more"
                    src="/images/screenshots/dark-user-device-information.webp"
                  />
                </picture>
                <picture>
                  <img
                    alt="All the core functionality you need, perfectly built"
                    src="/images/screenshots/dark-user-everything-else.webp"
                  />
                </picture>
              </figure>
            </div>
            </section>
          */}
        </div>
      </section>
      {/* ===================== Modern landing (post-hero) ===================== */}
      <section className="m-section" id="scale">
        <div className="m-container m-feature-grid">
          <div className="m-feature-head" data-reveal style={{ ['--i' as string]: 0 }}>
            <p className="m-eyebrow">From prototypes to the real world</p>
            <h2 className="m-h2">Save time and tokens. Never reinvent the wheel.</h2>
          </div>
          <div className="m-feature-text" data-reveal style={{ ['--i' as string]: 0 }}>
            <p className="m-lead">
              Code generation is now the easy part. Tests, migrations, monitoring and security are
              what separate a toy prototype from a business that grows on hard data.
            </p>
            <ul className="m-pill-row">
              {[
                'No lock-in',
                'Tested',
                'CI/CD',
                'Migrations',
                'Monitoring',
                'Logging',
                'Analytics',
                'Security',
              ].map((pill, i) => (
                <li key={pill} data-reveal style={{ ['--i' as string]: i + 1 }}>
                  <Icon name="check" />
                  {pill}
                </li>
              ))}
            </ul>
            <div className="m-cta-actions m-cta-actions--left">
              <a href="/#top" className="m-btn m-btn--primary" onClick={handleGetStarted}>
                Start building
              </a>
            </div>
          </div>
          <div className="m-feature-visual" data-reveal style={{ ['--i' as string]: 1 }}>
            {/* One build, the way the "How Molecule works" graphic shows it: the
                prompt, the packages Synthase picked (each links to its catalog
                page and explains itself on hover), and what it did with them. */}
            <div className="m-code m-build">
              <div className="m-code-bar">
                <span></span>
                <span></span>
                <span></span>
                <span className="m-code-file">synthase</span>
              </div>
              <div className="m-build-body">
                <p className="m-build-prompt">{DEMO_PROMPT}</p>
                <p className="m-label" data-step style={{ ['--i' as string]: 0 }}>
                  from the catalog
                </p>
                <ul className="m-pkg-row">
                  {BUILD_PACKAGES.map((p, i) => (
                    <li key={p.name} data-step style={{ ['--i' as string]: i + 1 }}>
                      <Link
                        className="m-pkg"
                        to={`/packages/${p.name}`}
                        style={{ ['--c' as string]: accent(i) }}
                        data-info={`@molecule/${p.name}: ${p.info}`}
                      >
                        {p.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <ul className="m-build-steps">
                  {BUILD_STEPS.map((step, i) => (
                    <li key={step} data-step style={{ ['--i' as string]: i + 10 }}>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="m-section m-section--alt" id="composable">
        <div className="m-container m-feature-grid">
          <div className="m-feature-text" data-reveal style={{ ['--i' as string]: 0 }}>
            <p className="m-eyebrow">Real architecture</p>
            <h2 className="m-h2">A step above vibe coding.</h2>
            <p className="m-lead">
              Not a throwaway prototype. Real, decoupled architecture that teams of seasoned
              professionals can own, audit, and scale. Every provider sits behind a swappable bond,
              so changing the database is one import, not a rewrite.
            </p>
            <div className="m-cats">
              <p className="m-label">
                <Link to="/packages">The same pattern for every category</Link>
              </p>
              <ul className="m-cat-row">
                {BOND_CATEGORIES.map((c, i) => (
                  <li key={c} data-step style={{ ['--i' as string]: i }}>
                    <Link
                      className="m-cat"
                      to={`/packages#q=${encodeURIComponent(`category:${c}`)}`}
                    >
                      {c}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="m-feature-sub">Frameworks and platforms swap the same way.</p>
            </div>
          </div>
          <div className="m-feature-visual" data-reveal style={{ ['--i' as string]: 1 }}>
            {/* The core interface the app code uses, the slot a bond fills (four
                database bonds take turns), and the one file that wires it. */}
            <div className="m-bond-stage">
              <div className="m-bond-core">
                <span className="m-label">core interface</span>
                <Link
                  className="m-bond-name"
                  to="/packages/api-database"
                  data-info="@molecule/api-database: the interface application code imports: findMany, create, update and the rest. Which database sits behind it is the bond's business."
                >
                  @molecule/api-database
                </Link>
                <span className="m-bond-methods">findMany · create · update …</span>
              </div>
              <span className="m-bond-link" aria-hidden="true" />
              <p className="m-label m-bond-slot-label">bond (provider)</p>
              <div className="m-bond-slot">
                {DB_BONDS.map((b, i) => (
                  <span
                    key={b.id}
                    className={`m-bond-chip m-cyc${i === 0 ? ' m-cyc--first' : ''}`}
                    style={{ ['--k' as string]: i, ['--c' as string]: accent(i) }}
                  >
                    <Link
                      to={`/packages/api-database-${b.id}`}
                      data-info={`The ${b.label} bond. Change this one import and the application code stays exactly as it is.`}
                    >
                      @molecule/api-database-{b.id}
                    </Link>
                    <em>{b.label}</em>
                  </span>
                ))}
              </div>
              <div className="m-code">
                <div className="m-code-bar">
                  <span></span>
                  <span></span>
                  <span></span>
                  <span className="m-code-file">api/src/bonds.ts</span>
                </div>
                <div className="m-code-body">
                  <span className="m-line m-dim">{'// the one wiring file'}</span>
                  <span className="m-line">{'import { pool, store } from'}</span>
                  <span className="m-line m-add">
                    {"  '@molecule/api-database-"}
                    <span className="m-cyc-word">
                      {DB_BONDS.map((b, i) => (
                        <span
                          key={b.id}
                          className={`m-cyc${i === 0 ? ' m-cyc--first' : ''}`}
                          style={{ ['--k' as string]: i }}
                        >
                          {b.id}
                          {"'"}
                        </span>
                      ))}
                    </span>
                  </span>
                  <span className="m-line">{'setPool(pool); setStore(store)'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="m-section" id="open">
        <div className="m-container">
          <p className="m-eyebrow">Open by design</p>
          <h2 className="m-h2">Open source, no lock-in.</h2>
          <div className="m-points">
            <div className="m-point" data-reveal style={{ ['--i' as string]: 0 }}>
              <span className="m-point-ico">
                <Icon name="law" />
              </span>
              <h3>Open source</h3>
              <p>
                Apache-2.0. <Link to="/packages">Browse the catalog</Link> and self-host everything.
              </p>
            </div>
            <div className="m-point" data-reveal style={{ ['--i' as string]: 1 }}>
              <span className="m-point-ico">
                <Icon name="pullRequest" />
              </span>
              <h3>Always expanding</h3>
              <p>New providers and integrations land constantly, from us and the community.</p>
            </div>
            <div className="m-point" data-reveal style={{ ['--i' as string]: 2 }}>
              <span className="m-point-ico">
                <Icon name="meter" />
              </span>
              <h3>Deploy with us</h3>
              <p>Optional. Pay only for the metered AI and infrastructure you use.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="m-section m-section--alt" id="capabilities">
        <div className="m-container">
          <h2 className="m-h2">Everything apps need.</h2>
          <p className="m-lead">
            Every app needs the same integrations (payments, uploads, notifications, etc.), and most
            AI tools regenerate them from scratch every time. Molecule already does this for you,
            battle-tested, so your tokens go toward what really matters.
          </p>
          <ul className="m-cap-list">
            {CAPABILITIES.map((c, i) => {
              const inner = (
                <>
                  <Icon name={c.icon} />
                  <span>{c.label}</span>
                  <span className="m-cap-check" aria-hidden="true">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 5.2 4 7.2 8 3" />
                    </svg>
                  </span>
                </>
              )
              return (
                <li key={c.label} data-reveal style={{ ['--i' as string]: i }} data-info={c.info}>
                  {c.category ? (
                    <Link
                      className="m-cap-item"
                      to={`/packages#q=${encodeURIComponent(`category:${c.category}`)}`}
                      aria-label={`${c.label}: ${c.info}`}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <span className="m-cap-item" tabIndex={0} aria-label={`${c.label}: ${c.info}`}>
                      {inner}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
          <div className="m-cap-callout" data-reveal>
            <span className="m-cap-callout-ico">
              <MoleculeSpinner size={54} gradient ariaLabel="Molecule" />
            </span>
            <div className="m-cap-callout-text">
              <h3>Built for AI, from the ground up.</h3>
              <p>
                Complete, tested packages you can drop in, documented so any AI agent can understand
                and wire them together: Synthase in the molecule.dev IDE, the mlcl CLI, or your own
                agent over MCP.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="m-section" id="loop">
        <div className="m-container m-feature-grid">
          <div className="m-feature-text" data-reveal style={{ ['--i' as string]: 0 }}>
            <p className="m-eyebrow">The loop</p>
            <h2 className="m-h2">Always improving.</h2>
            <p className="m-lead">
              Built-in analytics, error tracking and feedback let the AI keep improving the app from
              real use. Every release a little better, measured rather than guessed.
            </p>
            <div
              className="m-loop"
              role="img"
              aria-label="A loop: people use the app; analytics, errors and feedback come back; the AI reads the signals; it improves, ships and measures."
            >
              <div className="m-loop-face">
                <span className="m-loop-ring" />
                {/* One arrowhead at each quarter of the ring, turned to follow it clockwise. */}
                {[
                  { left: '50%', top: '0%', deg: 0 },
                  { left: '100%', top: '50%', deg: 90 },
                  { left: '50%', top: '100%', deg: 180 },
                  { left: '0%', top: '50%', deg: 270 },
                ].map((a) => (
                  <span
                    key={a.deg}
                    className="m-loop-arrow"
                    style={{ left: a.left, top: a.top, ['--deg' as string]: `${a.deg}deg` }}
                    aria-hidden="true"
                  >
                    <svg viewBox="-6 -6 12 12">
                      <path
                        d="M-4 -5 L4 0 L-4 5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ))}
                <span className="m-loop-orbit" aria-hidden="true">
                  <span className="m-loop-dot" />
                </span>
                <span className="m-loop-center">
                  every release
                  <br />a little better
                </span>
              </div>
              <ul className="m-loop-chips">
                {LOOP_STEPS.map((step, i) => (
                  <li
                    key={step.label}
                    className="m-loop-chip"
                    style={{
                      left: `${step.x}%`,
                      top: `${step.y}%`,
                      ['--c' as string]: accent(i),
                    }}
                  >
                    {step.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="m-feature-visual" data-reveal style={{ ['--i' as string]: 1 }}>
            <p className="m-label" data-step style={{ ['--i' as string]: 0 }}>
              from your running app
            </p>
            <ul className="m-signals">
              {LOOP_SIGNALS.map((sig, i) => (
                <li
                  key={sig.kind}
                  data-step
                  style={{ ['--i' as string]: i + 1, ['--c' as string]: accent(i) }}
                >
                  <Link
                    className="m-signal"
                    to={`/packages#q=${encodeURIComponent(sig.search)}`}
                    data-info={`Packages for ${sig.kind}: search the catalog for “${sig.search}”.`}
                  >
                    <span className="m-signal-kind">{sig.kind}</span>
                    <span className="m-signal-text">{sig.text}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <svg
              className="m-signal-arrow"
              viewBox="0 0 14 22"
              aria-hidden="true"
              data-step
              style={{ ['--i' as string]: 4 }}
            >
              <path
                d="M7 1v18M2 14l5 6 5-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="m-ai-card" data-step style={{ ['--i' as string]: 5 }}>
              <span className="m-label">the AI, in your workspace</span>
              <strong>Turns them into fixes and improvements to your app</strong>
              <span>Type-checked, tested and deployed. You approve, or let it run.</span>
            </div>
            <p className="m-result" data-step style={{ ['--i' as string]: 6 }}>
              each release measured against the last, not guessed
            </p>
            <p className="m-feature-sub" data-step style={{ ['--i' as string]: 7 }}>
              Swap the analytics provider; the instrumentation stays.
            </p>
          </div>
        </div>
      </section>

      <section className="m-section" id="faq">
        <div className="m-container">
          <h2 className="m-h2">Frequently asked questions</h2>
          <div className="m-faq-list">
            <details>
              <summary>What is Molecule.dev?</summary>
              <div>
                <p>
                  An open-source catalog of composable packages, and an AI agent, Synthase, that
                  assembles, runs and deploys full-stack apps from it. Use it from the molecule.dev
                  IDE, <Link to="/mlcl">from the command line with mlcl</Link>, or from any coding
                  agent through the MCP server.
                </p>
              </div>
            </details>
            <details>
              <summary>How does it work?</summary>
              <div>
                <p>
                  Describe what you want in your own words. Synthase picks packages from the
                  catalog, reads their generated docs and wires them like hand-written code, runs
                  the result in a live preview, and deploys it when you say so. Every app ships with
                  analytics, error tracking and user feedback built in, and the agent keeps
                  improving it from what they report.
                </p>
              </div>
            </details>
            <details>
              <summary>Can I use it without the IDE?</summary>
              <div>
                <p>
                  Yes. <code>mlcl create</code> scaffolds a project from the catalog on your own
                  machine, <code>mlcl mcp install</code> gives any coding agent the same packages,
                  docs and scaffolder, and every @molecule package installs like any other
                  dependency.
                </p>
              </div>
            </details>
            <details>
              <summary>What stack does it build with?</summary>
              <div>
                <p>
                  Any modern web framework, native mobile from the same code, and an API on any host
                  or database. Every provider sits behind a swappable bond, so nothing is tied to
                  one stack, and packages are added as stacks change.
                </p>
              </div>
            </details>
            <details>
              <summary>How is it different from other AI coding tools?</summary>
              <div>
                <p>
                  It composes tested, maintained packages instead of generating the same code again
                  for every project. That is faster, uses far fewer tokens, and leaves you with
                  real, decoupled architecture a team can own, audit and scale, not a throwaway
                  prototype.
                </p>
              </div>
            </details>
            <details>
              <summary>Can I use it with an app I already have?</summary>
              <div>
                <p>
                  Two ways. Connect your repo, and Synthase adds the features and integrations you
                  ask for directly to your codebase. Or install the open-source @molecule packages
                  into your project and wire them in with the{' '}
                  <a
                    href="https://github.com/molecule-dev/molecule#the-bond-system"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    bond pattern
                  </a>
                  , like any dependency.
                </p>
              </div>
            </details>
            <details>
              <summary>How do I find the right package or template?</summary>
              <div>
                <p>
                  Search the <Link to="/packages">catalog</Link> by name, by category (
                  <code>category:auth</code>), by what a package exports, or in plain words like
                  “user feedback”. <Link to="/templates">Templates</Link> work the same way. Press{' '}
                  <code>/</code> on either page to search, and every search is a link you can share.
                </p>
              </div>
            </details>
            <details>
              <summary>Is there any lock-in?</summary>
              <div>
                <p>
                  None. You own the code, real and readable, built on open-source packages
                  (Apache-2.0). Swap any provider, export it with its database and keys, self-host
                  anything.
                </p>
              </div>
            </details>
            <details>
              <summary>What does it cost?</summary>
              <div>
                <p>
                  Free to self-host. Build and deploy on molecule.dev and pay only for the AI and
                  infrastructure you use.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      <section className="m-section m-cta" id="get-started">
        <div className="m-container">
          <div className="m-cta-head" data-reveal style={{ ['--i' as string]: 0 }}>
            <p className="m-eyebrow">Start</p>
            <h2 className="m-h2">Three ways in.</h2>
            <p className="m-lead">
              The same open-source packages and the same agent, from wherever you already work.
            </p>
          </div>
          <ul className="m-ways">
            {WAYS.map((w, i) => (
              <li key={w.title} data-reveal style={{ ['--i' as string]: i + 1 }}>
                <span className="m-label">{w.label}</span>
                <h3>{w.title}</h3>
                {w.command ? (
                  <CopyCommand command={w.command} />
                ) : (
                  <a href="/#top" className="m-btn m-btn--primary" onClick={handleGetStarted}>
                    Start building
                  </a>
                )}
                <p>{w.body}</p>
                {w.docs ? (
                  <p className="m-way-docs">
                    <Link to={w.docs.to}>{w.docs.label}</Link>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="m-cta-close" data-reveal style={{ ['--i' as string]: 0 }}>
            <strong>Plain code you own. Export it, with its database and keys, at any time.</strong>
            <span>Apache-2.0 · no lock-in · any editor, agent or CI.</span>
          </p>
          <div className="m-cta-actions" data-reveal style={{ ['--i' as string]: 1 }}>
            <a href="/#top" className="m-btn m-btn--primary" onClick={handleGetStarted}>
              Get started
            </a>
            <a
              href="https://github.com/molecule-dev/molecule"
              rel="noopener noreferrer"
              target="_blank"
              className="m-btn m-btn--ghost"
            >
              Browse on GitHub
            </a>
          </div>
        </div>
      </section>
      <SiteFooter onGetStarted={handleGetStarted} />
      <EarlyAccessModal
        open={earlyOpen}
        prompt={pendingPrompt}
        onClose={() => setEarlyOpen(false)}
      />
    </LandingFrame>
  )
}
